import { createContext, type ReactNode, useContext } from "react";
import { isWeb } from "tamagui";
import { ChevronLeft as ChevronLeftIcon, X as XIcon } from "@tamagui/lucide-icons";
import { useLiquidGlass } from "../utils/useLiquidGlass";
import { Button, type ButtonProps } from "./Button";
import { GlassScope } from "./Glass/GlassScope";
import { GlassTheme } from "./Glass/GlassTheme";
import type { IconComponent } from "./Icon";
import { XStack } from "./Stacks";
export type HeaderTone = "default" | "inverse";
type InHeaderValue = {
  tone: HeaderTone;
  systemGlass: boolean;
};
const InHeaderContext = createContext<InHeaderValue | null>(null);

/** Marks its children as header content, in the header's tone. `systemGlass`: iOS 26 wraps this slot in its own glass (bar items, not the title). */
export const InHeader = ({
  tone,
  systemGlass = false,
  children
}: {
  tone: HeaderTone;
  systemGlass?: boolean;
  children: ReactNode;
}) => <InHeaderContext.Provider value={{
  tone,
  systemGlass
}} data-sentry-element="InHeaderContext.Provider" data-sentry-component="InHeader" data-sentry-source-file="HeaderButton.tsx">
    {children}
  </InHeaderContext.Provider>;

/** The header this sits in, or null outside one. */
export const useInHeader = () => useContext(InHeaderContext);
export type HeaderButtonProps = Pick<ButtonProps, "onPress" | "disabled"> & ({
  kind: "back" | "close";
  icon?: never;
  label?: string;
} | {
  kind?: never;
  icon: IconComponent;
  label: string;
});
const kinds = {
  back: {
    icon: ChevronLeftIcon,
    label: "Back"
  },
  close: {
    icon: XIcon,
    label: "Close"
  }
};

/** iOS 26 wraps header buttons in its own glass, so they're plain icons there; elsewhere we draw the bubble. */
export const HeaderButton = (props: HeaderButtonProps) => {
  const header = useInHeader();
  const liquidGlass = useLiquidGlass();
  const tone = header?.tone ?? "default";
  const systemGlass = header ? header.systemGlass : liquidGlass;
  const {
    icon: Icon,
    label
  } = props.kind === undefined ? props : {
    icon: kinds[props.kind].icon,
    label: props.label ?? kinds[props.kind].label
  };
  if (systemGlass) {
    return <XStack ai="center" jc="center" transition="press" pressStyle={{
      scale: 0.97
    }} p="$1" disabled={props.disabled} onPress={props.onPress} accessible accessibilityRole="button" accessibilityLabel={label}>
        <Icon size="$2" strokeWidth={2} color={tone === "inverse" ? "$iconInverse" : "$iconSubtle"} />
      </XStack>;
  }
  const button = <Button variant="glass" flat={!isWeb} icon={Icon} aria-label={label} onPress={props.onPress} disabled={props.disabled} />;
  return tone === "inverse" ? <GlassTheme appearance="dark" data-sentry-element="GlassTheme" data-sentry-component="HeaderButton" data-sentry-source-file="HeaderButton.tsx">
      <GlassScope prominence="prominent" data-sentry-element="GlassScope" data-sentry-source-file="HeaderButton.tsx">{button}</GlassScope>
    </GlassTheme> : button;
};